// =============================================================================
// Admin Analytics — page shell (PROA-1474)
// =============================================================================
// Header ("Analytics") + Reports/Activation tabs + sticky global filter bar
// (date range with comparison period, and template-type + template-name filters
// that are hidden on the Activation view). Widget content lives in the view files.
// =============================================================================
import { clsx } from "clsx";
import { useEffect, useRef } from "react";

import {
  Button,
  CalendarDateRange,
  CalendarPicker,
  Headline,
  SearchableDropdown,
  SearchableDropdownItem,
  TabNavigation,
} from "@fronterahealth/frontera-ui-components";

import { AdminAnalyticsTab, useAdminAnalytics } from "@pages/AdminAnalytics/AdminAnalyticsProvider";
import { useTemplateOptions } from "@pages/AdminAnalytics/data/hooks";
import { TEMPLATE_TYPES, TEMPLATE_TYPE_LABELS, TemplateType } from "@pages/AdminAnalytics/data/types";
import { DEFAULT_RANGE_LABEL, formatTemplateName } from "@pages/AdminAnalytics/helpers";
import { ActivationView } from "@pages/AdminAnalytics/views/ActivationView";
import { ReportsView } from "@pages/AdminAnalytics/views/ReportsView";

const TABS: AdminAnalyticsTab[] = ["Reports", "Activation"];

/** Keyframes + checkbox border fix (Mosaic SearchableDropdown applies border-2 only on hover,
 *  causing a 1px→2px jump that makes the checkbox look like it's shrinking). */
const MotionStyles = () => (
  <style>{`
    @keyframes aa-rise { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
    @keyframes aa-fade { from { opacity: 0; } to { opacity: 1; } }
    [data-testid="mapped-options"] .h-4.w-4 { border-width: 1px !important; }
    body.date-chip-open [data-popper-placement] { z-index: 202 !important; }
  `}</style>
);

// ----- Filter bar ------------------------------------------------------------

const FilterBar: React.FC = () => {
  const {
    globalRange,
    setGlobalRange,
    selectedTab,
    templateType,
    setTemplateType,
    templateName,
    setTemplateName,
    orgConfig,
  } = useAdminAnalytics();

  const reportTypeItems: SearchableDropdownItem[] = TEMPLATE_TYPES.filter((t) =>
    orgConfig.enabledTemplates.includes(t),
  ).map((t) => ({ id: t, label: TEMPLATE_TYPE_LABELS[t] }));

  const templateOptions = useTemplateOptions();
  const templateNameItems: SearchableDropdownItem[] = [...(templateOptions ?? [])]
    .sort((a, b) => b.count - a.count)
    .map((o) => ({ id: o.templateGroup, label: formatTemplateName(o.templateGroup) }));

  const hasActiveFilters = templateType.length > 0 || templateName.length > 0;
  // Report Type filter only makes sense when the org has multiple enabled types.
  // Template Name filter is always useful on the Reports tab regardless of type count.
  const showReportTypeFilter = selectedTab === "Reports" && orgConfig.enabledTemplates.length > 1;
  const showTemplateFilters = selectedTab === "Reports";

  return (
    // backdrop-blur intentionally omitted — it creates a CSS containing block that breaks
    // Popper.js's position:fixed strategy, causing the CalendarPicker dropdown to misplace.
    <div className="sticky top-0 z-10 -mx-4 flex flex-wrap items-center gap-3 border-b border-limestone-100 bg-limestone-50 px-4 py-3">
      {showTemplateFilters && (
        <>
          {showReportTypeFilter && (
            <SearchableDropdown
              label="Report Type"
              placeholder="All types"
              items={reportTypeItems}
              selectedItems={templateType.map((t) => ({ id: t, label: TEMPLATE_TYPE_LABELS[t] }))}
              onSelectionChange={(items) => setTemplateType(items.map((i) => i.id as TemplateType))}
              showSearch={false}
              withClear
            />
          )}
          <SearchableDropdown
            label="Template"
            placeholder="All templates"
            items={templateNameItems}
            selectedItems={templateName.map((n) => ({ id: n, label: formatTemplateName(n) }))}
            onSelectionChange={(items) => setTemplateName(items.map((i) => i.id))}
            showSearch={false}
            withClear
          />
          {hasActiveFilters && (
            <button
              type="button"
              onClick={() => {
                setTemplateType([]);
                setTemplateName([]);
              }}
              className="text-xs text-text-secondary underline-offset-2 hover:text-text-primary hover:underline"
            >
              Clear all
            </button>
          )}
        </>
      )}
      {selectedTab === "Activation" && (
        <span className="text-xs text-text-secondary">Template filter doesn't apply to activation metrics</span>
      )}

      <div className="flex-1" />

      <CalendarPicker
        comparisonEnabled
        placeholder="Custom"
        popperPlacement="bottom-end"
        selectedDateRange={globalRange}
        inputSize="sm"
        onDateChange={(range: CalendarDateRange) => setGlobalRange(range)}
        mode="filter"
        selectedLabel={globalRange?.selectedLabel ?? DEFAULT_RANGE_LABEL}
        selectedComparisonLabel="Previous Period"
        presetLabels={[
          "Last 7 Days",
          "Last 2 Weeks",
          "Last 30 Days",
          "Last 3 Months",
          "Last 6 Months",
          "Last Year",
          "Custom",
        ]}
      />
    </div>
  );
};

const ViewTabs: React.FC = () => {
  const { selectedTab, setSelectedTab } = useAdminAnalytics();
  return (
    <div className="min-w-full overflow-x-auto no-scrollbar">
      <TabNavigation
        edgeToEdge
        tabs={TABS.map((tab) => {
          const comingSoon = tab === "Activation";
          return {
            current: selectedTab === tab,
            type: "component",
            name: tab,
            Component: (
              <Button
                text={
                  <div className="flex w-full flex-row items-center justify-center gap-2 font-normal">
                    {tab}
                    {comingSoon && (
                      <span className="rounded-full bg-limestone-100 px-1.5 py-0.5 text-[10px] text-limestone-700">
                        coming soon
                      </span>
                    )}
                  </div>
                }
                variant="primary"
                buttonStyle="ghost"
                className={clsx(
                  selectedTab === tab ? "text-limestone-900" : "text-text-secondary",
                  comingSoon ? "cursor-default opacity-60" : "hover:text-primary",
                )}
                onClick={comingSoon ? undefined : () => setSelectedTab(tab)}
              />
            ),
          };
        })}
      />
    </div>
  );
};

export const AdminAnalytics: React.FC = () => {
  const { selectedTab } = useAdminAnalytics();
  const rootRef = useRef<HTMLDivElement>(null);

  // Land at the top of the page. Without this the sticky filter bar's date
  // control gets focused on mount and the browser scrolls it into view, leaving
  // the page opened mid-scroll. Reset the scroll parent (and window) to the top,
  // once now and once after paint to beat the focus-driven scroll.
  useEffect(() => {
    let el: HTMLElement | null = rootRef.current?.parentElement ?? null;
    while (el) {
      const overflowY = getComputedStyle(el).overflowY;
      if ((overflowY === "auto" || overflowY === "scroll") && el.scrollHeight > el.clientHeight) break;
      el = el.parentElement;
    }
    const toTop = () => {
      el?.scrollTo({ top: 0 });
      window.scrollTo({ top: 0 });
    };
    toTop();
    const raf = requestAnimationFrame(toTop);
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <div ref={rootRef} className="mx-auto flex w-full flex-col gap-4 px-4">
      <MotionStyles />
      <Headline size="medium" className="mt-10">
        Analytics
      </Headline>
      <ViewTabs />
      <FilterBar />
      <div key={selectedTab} className="pb-8" style={{ animation: "aa-fade 220ms ease-out" }}>
        {selectedTab === "Reports" ? <ReportsView /> : <ActivationView />}
      </div>
    </div>
  );
};
